import { useEffect, useState } from "react";
import { IKeyValue } from "../../../../components/Administrations/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { setErrorMsg, setOpenToast } from "../../../../store/helpers/helperReducers";
import { store } from "../../../../store";
import { emailSmtpPayload, resetSmtpServer } from "../../../../components/helpers";
import { useGetEmailSmtpQuery, useSendTestEmailMutation, useUpdateSmtpEmailMutation } from "../../../../store/api/admin/adminApiSlice";
import { selectedSmtpEmailSelector } from "../../../../store/api/admin/selectors";
import { useSelector } from "react-redux";
import { ISmtpEmail } from "../../../../store/api/admin/types";
import { setSelectedSmtpEmail } from "../../../../store/api/admin/reducers";
import { isValidEmail, isValidPassword } from "../../../../utils/validations";
import { errorMessage, handleBooleanStringValues } from "../../../../utils/helpers";

export const useSmtpEmailForm = () => {
  const dispatch = useStoreDispatch();
  const [errMsg, setErrMsg] = useState<IKeyValue>({});
  const smtpEmailDetails = useSelector(selectedSmtpEmailSelector);
  const [SmtpServerData, setSmtpServerData] = useState<ISmtpEmail>(smtpEmailDetails);
  const [enableSmtp, setEnableSmtp] = useState(true);
  const [selectedSecurity, setSelectedSecurity] = useState("none");

  // let errCount = 0;

  const { data, refetch } = useGetEmailSmtpQuery();

  const [updateEmailSmtp] = useUpdateSmtpEmailMutation();

  const [sendTestEmail] = useSendTestEmailMutation();

  useEffect(() => {
    if (data) {
      // handle dispatch to store data in smtpEmail state
      let obj = {
        useSmtpAuth: handleBooleanStringValues(data?.useSmtpAuth) || false,
        smtpHost: data?.smtpHost || "",
        smtpPort: data?.smtpPort || "25",
        smtpUsername: data?.smtpUsername || "",
        smtpPassword: data?.smtpPassword || "",
        emailKey: data?.emailKey || "",
        useSmtpSsl: handleBooleanStringValues(data?.useSmtpSsl) || false,
        fromEmail: data?.fromEmail || "",
        useSmtpTls: handleBooleanStringValues(data?.useSmtpTls) || false,
        testEmail: data?.testEmail || "",
      };
      dispatch(setSelectedSmtpEmail(obj));
      setSmtpServerData(obj);
      // Set enableSmtp based on whether there's a configured smtpHost (not just the default)
      setEnableSmtp(!!data?.smtpHost);
      if (obj?.useSmtpSsl) setSelectedSecurity("ssl");
      if (obj?.useSmtpTls) setSelectedSecurity("startls");
    }
  }, [data, dispatch, refetch]);

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    const existing = store.getState().helpers.errorMsg;

    const setErr = (field: string, msg: string) => {
      dispatch(setErrorMsg({ ...existing, [field]: msg }));
    };
    const clearErr = (field: string) => {
      const cleared: Record<string, string> = {};
      for (const [k, v] of Object.entries(existing)) {
        if (k !== field) cleared[k] = String(v ?? "");
      }
      dispatch(setErrorMsg(cleared));
    };

    if (name === "enableSmtp" && !SmtpServerData?.useSmtpAuth) {
      setErr(name, `Please enable Snmpv1v2!!`);
    }
    if (name === "smtpPort") {
      if (!value) setErr(name, `Port is required!!`);
      else clearErr(name);
    }
    if (name === "smtpHost") {
      if (!value) setErr(name, `smtpHost is required!!`);
      else clearErr(name);
    }
    if (name === "fromEmail") {
      if (!isValidEmail(value)) setErr(name, `Invalid Email`);
      else clearErr(name);
    }
    if (name === "smtpUsername") {
      if (!value) setErr(name, `Username is required!`);
      else clearErr(name);
    }
    if (name === "smtpPassword") {
      if (!value) setErr(name, `Password is required!`);
      else if (!isValidPassword(value)) setErr(name, `Password is invalid. It must meet the specified criteria!`);
      else clearErr(name);
    }
    if (name === "testEmail") {
      if (!isValidEmail(value)) setErr(name, `Invalid Email`);
      else clearErr(name);
    }
  };

  const handleValidateSmtp = (testKey?: string) => {
    // If SMTP is not enabled, skip validation
    if (!enableSmtp) return true;
    const existing = store.getState().helpers.errorMsg;

    let isValid: boolean = true;
    if (SmtpServerData?.fromEmail) {
      if (!isValidEmail(SmtpServerData?.fromEmail)) {
        dispatch(setErrorMsg({ ...existing, fromEmail: `Invalid Email` }));
        isValid = false;
      }
    }
    if (SmtpServerData?.useSmtpAuth) {
      const { smtpUsername, smtpPassword } = SmtpServerData;
      if (!smtpUsername) {
        dispatch(setErrorMsg({ ...existing, smtpUsername: `Username is required!.` }));
        isValid = false;
      }
      if (smtpPassword) {
        if (!isValidPassword(smtpPassword)) {
          dispatch(setErrorMsg({ ...existing, smtpPassword: `Password is invalid. It must meet the specified criteria!` }));
          isValid = false;
        }
      }
      if (!smtpPassword) {
        dispatch(setErrorMsg({ ...existing, smtpPassword: `Password is required!.` }));
        isValid = false;
      }
    }
    if (!SmtpServerData?.smtpPort) {
      dispatch(setErrorMsg({ ...existing, smtpPort: `Port is required!!` }));
      isValid = false;
    }
    if (!SmtpServerData?.smtpHost) {
      dispatch(setErrorMsg({ ...existing, smtpHost: `smtpHost is required!!` }));
      isValid = false;
    }

    if (testKey === "test") {
      if (!isValidEmail(SmtpServerData?.testEmail)) {
        dispatch(setErrorMsg({ ...existing, testEmail: `Invalid Email` }));
        isValid = false;
      }
    }
    return isValid;
  };


  const submitHandler = async (): Promise<boolean> => {
    const hasErros = enableSmtp ? Object.values(store.getState().helpers.errorMsg).some((err) => err !== "") : false;
    if (handleValidateSmtp() && !hasErros) {
      try {
        let result: any;
        result = await updateEmailSmtp(emailSmtpPayload(SmtpServerData, selectedSecurity, data?.testEmail));
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: "The data has been updated for Email Smtp",
            })
          );
          refetch();
          return true;
        }
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
    return false;
  };

  const submitTestEmailHandler = async () => {
    // Only allow sending test email when SMTP is enabled
    if (!enableSmtp) return;

    const hasErros = Object.values(store.getState().helpers.errorMsg).some((err) => err !== "");
    if (handleValidateSmtp("test") && !hasErros && SmtpServerData?.testEmail) {
      let payload  = {
        emailAddress: SmtpServerData?.testEmail || ""
      }
      try {
        let result: any;
        result = await sendTestEmail(payload);
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: "Test Email has been Sent successfully.",
            })
          );
          refetch();
        }
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleResetTrapRecieve = () => {
    const resetData = smtpEmailDetails ? { ...smtpEmailDetails } : { ...resetSmtpServer };
    setSmtpServerData(resetData);
    setEnableSmtp(!!resetData?.smtpHost);
    if (resetData?.useSmtpSsl) setSelectedSecurity("ssl");
    else if (resetData?.useSmtpTls) setSelectedSecurity("startls");
    else setSelectedSecurity("none");
    setErrMsg({});
    dispatch(setErrorMsg({}));
  };

  return {
    errMsg,
    setErrMsg,
    SmtpServerData,
    setSmtpServerData,
    onBlurHandler,
    handleResetTrapRecieve,
    submitHandler,
    enableSmtp,
    setEnableSmtp,
    smtpEmailDetails,
    selectedSecurity,
    setSelectedSecurity,
    submitTestEmailHandler
  };
};
